// @pages/feedback/FeedbackSubmission.page.tsx

import React, { useCallback, useMemo } from 'react';
import FeedbackSubmissionView from '@view/workspace/FeedbackSubmission';
import { useLoadingController } from '@contexts/Loading.context';
import { useNavigate } from "react-router-dom";
import { renderRoute, routeDocumentSelection } from "@navigation/typedRoutes";
import { useFeedbackClient } from "@contexts/FeedbackClient.context";

import { ok } from "@ps-bridge/ok";
import { mkNavigate, mkTaskRunner } from "@ps-bridge/capabilities";
import { ioEffect } from '@utils/effects/branded';
import type { FeedbackSubmissionKernel } from "@ps-bridge/contracts/feedbackSubmission";
import { mkFeedbackSubmissionController } from "@ps-bridge/controllers/feedbackSubmission";
import { buildFeedbackSubmissionViewProps } from "@ps-bridge/feedbackSubmission.program";
import { useIoState } from "@utils/useIoState";
import { useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import type { TaskTs } from "@ps-bridge/interop";
import type { Ok } from "@ps-bridge/ok";

const FeedbackSubmissionPage: React.FC = () => {
    const navigate = useNavigate();
    const feedbackClient = useFeedbackClient();
    const loading = useLoadingController();
    const feedbackContents = useIoState<string>("");
    const showSuccess = useIoState<boolean>(false);
    const feedbackContentsRead = feedbackContents.read;
    const feedbackContentsSet = feedbackContents.set;
    const showSuccessRead = showSuccess.read;
    const showSuccessSet = showSuccess.set;
    const runTaskWithError = useRunTaskWithError();
    const runTaskWithErrorIo = useCallback(
        (task: TaskTs<Ok>) =>
            ioEffect(() => {
                runTaskWithError(task).then(() => ok);
                return ok;
            }),
        [runTaskWithError],
    );
    const taskRunner = useMemo(() => mkTaskRunner(runTaskWithErrorIo), [runTaskWithErrorIo]);
    const navigateCapability = useMemo(
        () =>
            mkNavigate((route) =>
                ioEffect(() => {
                    navigate(renderRoute(route));
                    return ok;
                }),
            ),
        [navigate],
    );
    const controller = useMemo(
        () =>
            mkFeedbackSubmissionController({
                taskRunner,
                loading,
                feedbackClient,
                navigate: navigateCapability,
                form: {
                    feedbackContents: {
                        read: feedbackContentsRead,
                        set: feedbackContentsSet,
                    },
                    showSuccess: {
                        read: showSuccessRead,
                        set: showSuccessSet,
                    },
                },
            }),
        [
            feedbackClient,
            feedbackContentsRead,
            feedbackContentsSet,
            loading,
            navigateCapability,
            showSuccessRead,
            showSuccessSet,
            taskRunner,
        ],
    );
    const kernel: FeedbackSubmissionKernel = useMemo(
        () => ({
            controller,
            loading,
            feedbackClient,
            navigate: navigateCapability,
            form: {
                feedbackContents: {
                    read: feedbackContentsRead,
                    set: feedbackContentsSet,
                },
                showSuccess: {
                    read: showSuccessRead,
                    set: showSuccessSet,
                },
            },
        }),
        [
            controller,
            feedbackClient,
            feedbackContentsRead,
            feedbackContentsSet,
            loading,
            navigateCapability,
            showSuccessRead,
            showSuccessSet,
        ],
    );
    const viewProps = useMemo(
        () =>
            buildFeedbackSubmissionViewProps({
                kernel,
                documentSelectionRoute: routeDocumentSelection,
            }),
        [kernel],
    );

    return (
        <FeedbackSubmissionView
            {...viewProps}
        />
    );
};

export default FeedbackSubmissionPage;
